import { useApp } from 'contexts/app';
import { useSnackbar } from 'contexts/snackbar';
import { isEqual } from 'lodash';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Text } from 'shell/components';
import { SYSTEM_ASSIGNMENT_DEFAULTS } from 'utils/assignmentDefaults';
import { getFeedbackSettingsErrors } from 'utils/assignmentSettingsValidation';
import { stripHTML } from 'utils/helper';
import AssignmentFeedbackSettingsSummary from './AssignmentFeedbackSettingsSummary';
import AssignmentSettingsPanel from './AssignmentSettingsPanel';
import { AssignmentSettingToggleCard } from './AssignmentSettingsPanelControls';
import FeedbackSettingsForm from './FeedbackSettingsForm';
import FeedbackTypeCard from './FeedbackTypeCard';
import StudentFeedbackSettingsForm from './StudentFeedbackSettingsForm';

const AssignmentFeedbackPanel = ({ form, setForm, errors, setErrors, isEditable, student = false }) => {
    const { isFeedbackFeatureDisabled, isGradingFeatureDisabled } = useApp();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { t: settingsT } = useTranslation('assignmentSettings');
    const id = useId();
    const field = student ? 'studentFeedbackSettings' : 'feedbackSettings';
    const contextField = student ? 'studentFeedbackCustomContext' : 'feedbackCustomContext';
    const enabledField = student ? 'aiStudentFeedbackEnabled' : 'aiFeedbackEnabled';
    // Institutional assignment-feedback restrictions apply to final submissions, as in the existing student dialog.
    const restricted = !student && isFeedbackFeatureDisabled;
    const enabled = form[enabledField] && !restricted;
    const disabled = !isEditable || !enabled;
    const settings = { ...SYSTEM_ASSIGNMENT_DEFAULTS[field], ...form[field], [contextField]: form[contextField] || '' };
    const assignmentFeedbackSettings = { ...SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings, ...form.feedbackSettings };
    const assignmentFeedbackInstructions = form.feedbackCustomContext || '';
    const formativeSettings = { ...settings };
    delete formativeSettings[contextField];
    const isCopyDisabled =
        isEqual(assignmentFeedbackSettings, formativeSettings) &&
        stripHTML(assignmentFeedbackInstructions).trim() === stripHTML(form.studentFeedbackCustomContext || '').trim();
    const detailErrors = getFeedbackSettingsErrors(form, student, { isGradingFeatureDisabled });
    const showValidationErrors = Boolean(errors?.[field]);
    const visibleDetailErrors = showValidationErrors ? detailErrors : {};
    const Form = student ? StudentFeedbackSettingsForm : FeedbackSettingsForm;
    const handleChange = (changes) => {
        if (disabled) return;
        if (changes?.structure && changes.structure !== settings.structure) {
            setErrors?.((current) => {
                if (!current?.[field]) return current;
                const next = { ...current };
                delete next[field];
                return next;
            });
        }
        setForm((current) => {
            const value = {
                ...SYSTEM_ASSIGNMENT_DEFAULTS[field],
                ...current[field],
                [contextField]: current[contextField] || '',
            };
            const next = typeof changes === 'function' ? changes(value) : { ...value, ...changes };
            const { [contextField]: context, ...nextSettings } = next;
            return { ...current, [field]: nextSettings, [contextField]: context };
        });
    };
    const handleCopyAssignmentFeedbackSettings = () => {
        setForm((current) => ({
            ...current,
            studentFeedbackSettings: { ...current.feedbackSettings },
            studentFeedbackCustomContext: current.feedbackCustomContext || '',
        }));
        message.success(t('message.feedback-settings-copy-success', 'Feedback settings have copied successfully.'));
    };
    return (
        <AssignmentSettingsPanel
            enabled={enabled}
            toggle={
                <AssignmentSettingToggleCard
                    title={
                        student
                            ? t('formative-feedback.student-title', 'Formative Feedback for Students')
                            : t(
                                  'feedback-section.final-submission-feedback-suggestions',
                                  'Final Submission Feedback Suggestions'
                              )
                    }
                    description={
                        student
                            ? t(
                                  'formative-feedback.student-description',
                                  'Students can generate up to 3 rounds of AI feedback for assignments drafts before they submit. When you enable instant feedback, students will be able to see the assignment description and the assignment rubric.'
                              )
                            : t(
                                  'feedback-section.feedback-description',
                                  "TimelyGrader leverages assignment details to provide instructors with personalized feedback suggestions for student's final submissions."
                              )
                    }
                    checked={enabled}
                    disabled={!isEditable}
                    restricted={restricted}
                    onChange={(value) => setForm((current) => ({ ...current, [enabledField]: value }))}
                />
            }
        >
            {student && (
                <Text component={'h2'} variant={'p1'} weight={'medium'}>
                    {t('feedback-section.feedback-structure-label', 'Feedback Structure')}
                </Text>
            )}
            {student && (
                <AssignmentFeedbackSettingsSummary
                    settings={assignmentFeedbackSettings}
                    additionalInstructions={assignmentFeedbackInstructions}
                    onCopy={handleCopyAssignmentFeedbackSettings}
                    isCopyDisabled={isCopyDisabled}
                    t={t}
                />
            )}
            <Stack spacing={2} sx={{ width: '100%', maxWidth: '900px', mx: 'auto', minWidth: 0 }}>
                <Text component={'h2'} variant={'p1'} weight={'medium'}>
                    {student
                        ? t('formative-feedback.structure-label', 'Formative Feedback Structure')
                        : t('feedback-section.feedback-structure-label', 'Feedback Structure')}
                </Text>
                <Stack
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        gap: 3,
                        p: 1,
                        '@media (max-width: 899px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
                        '@media (max-width: 599px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
                    }}
                >
                    {['rubric', 'system', 'templated', 'custom'].map((type) => (
                        <FeedbackTypeCard
                            key={type}
                            type={type}
                            isSelected={settings.structure === type}
                            disabled={
                                disabled ||
                                (type === 'templated' && (!form.aiGradingEnabled || isGradingFeatureDisabled))
                            }
                            onClick={() => handleChange({ structure: type })}
                        />
                    ))}
                </Stack>
                {(!form.aiGradingEnabled || isGradingFeatureDisabled) && (
                    <Text color={'light'}>
                        {settingsT(
                            'panels.feedback.templatesRequireGrading',
                            'Enable grading suggestions to use Smart Feedback Templates.'
                        )}
                    </Text>
                )}
            </Stack>
            <Form
                data={form}
                form={settings}
                errors={visibleDetailErrors}
                handleChange={handleChange}
                disabled={disabled}
                idPrefix={`${id}-`}
            />
        </AssignmentSettingsPanel>
    );
};
export default AssignmentFeedbackPanel;
